import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';

// Blocking "saving in progress" overlay shown while the back end persists
// changes to the LOS. It intercepts all interaction so users cannot click
// away mid-save (which can cancel the in-flight operation).
//
// Copy rule: never reference the LOS vendor name in user-facing text — use
// "the LOS" or "our system" instead.

const ROTATE_INTERVAL_MS = 2500;
const SLOW_PATH_MS = 10000;
const DEFAULT_TIMEOUT_MS = 75000; // Between 60–90s; never blocks indefinitely.

export const DEFAULT_HEADLINE = "Hang tight — we're saving your changes";
export const DEFAULT_BODY =
  "This usually takes just a few seconds. Clicking around while we work can undo what's in flight, so sit back and let us finish.";
export const DEFAULT_MESSAGES = [
  'Syncing with the LOS…',
  'Locking in the details…',
  'Double-checking the numbers…',
  'Almost done…',
];
export const DEFAULT_SLOW_MESSAGE =
  'Still working — our system is taking its time. Almost there.';
export const DEFAULT_QUOTE = {
  text: 'Patience is bitter, but its fruit is sweet.',
  author: 'Jean-Jacques Rousseau',
};
export const DEFAULT_TIMEOUT_HEADLINE = 'This is taking longer than expected';
export const DEFAULT_TIMEOUT_BODY =
  "We haven't heard back from our system yet. Your changes may still be processing. You can retry or refresh to check the latest status.";

const LogoChip = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#ffffff',
      borderRadius: 2,
      px: 1.5,
      py: 0.75,
      boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
    }}
  >
    <Box
      component="img"
      src="/assets/pro-portal-mark.svg"
      alt="PRO Portal"
      sx={{ height: 26, width: 'auto', display: 'block' }}
    />
  </Box>
);

/**
 * Presentational blocking save-progress modal. Prefer the `useSaveProgress`
 * hook, which drives this component through an operation's lifecycle.
 */
export const SaveProgressOverlay = ({
  open,
  status = 'saving', // 'saving' | 'timeout'
  headline = DEFAULT_HEADLINE,
  body = DEFAULT_BODY,
  messages = DEFAULT_MESSAGES,
  slowMessage = DEFAULT_SLOW_MESSAGE,
  quote = DEFAULT_QUOTE,
  timeoutHeadline = DEFAULT_TIMEOUT_HEADLINE,
  timeoutBody = DEFAULT_TIMEOUT_BODY,
  canRetry = false,
  onRetry,
  onRefresh,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [showSlow, setShowSlow] = useState(false);

  const activeMessages = messages?.length ? messages : DEFAULT_MESSAGES;
  const isSaving = status === 'saving';

  // Rotate the status line and reveal the slow-path reassurance while saving.
  useEffect(() => {
    if (!open || !isSaving) {
      setMessageIndex(0);
      setShowSlow(false);
      return undefined;
    }

    setMessageIndex(0);
    setShowSlow(false);

    const rotate = setInterval(() => {
      setMessageIndex((i) => (i + 1) % activeMessages.length);
    }, ROTATE_INTERVAL_MS);
    const slow = setTimeout(() => setShowSlow(true), SLOW_PATH_MS);

    return () => {
      clearInterval(rotate);
      clearTimeout(slow);
    };
  }, [open, isSaving, activeMessages.length]);

  return (
    <Modal
      open={!!open}
      disableEscapeKeyDown
      // No onClose handler that dismisses — the overlay is not user-dismissible.
      aria-labelledby="save-progress-headline"
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(15, 18, 20, 0.72)' },
        },
      }}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Fade in={!!open}>
        <Box
          sx={{
            outline: 'none',
            width: '100%',
            maxWidth: 420,
            mx: 2,
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            color: 'common.white',
            bgcolor: 'rgba(28, 32, 36, 0.96)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Stack spacing={2.5} alignItems="center">
            <LogoChip />

            {isSaving ? (
              <>
                <Box sx={{ position: 'relative', display: 'inline-flex', mt: 0.5 }}>
                  <CircularProgress
                    size={56}
                    thickness={4}
                    sx={{ color: 'secondary.main' }}
                  />
                </Box>

                <Box>
                  <Typography
                    id="save-progress-headline"
                    variant="h5"
                    sx={{ color: 'common.white', mb: 1 }}
                  >
                    {headline}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 340, mx: 'auto' }}
                  >
                    {body}
                  </Typography>
                </Box>

                <Box sx={{ minHeight: 22 }}>
                  <Fade in key={messageIndex} timeout={400}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'secondary.light', fontWeight: 600 }}
                      aria-live="polite"
                    >
                      {activeMessages[messageIndex]}
                    </Typography>
                  </Fade>
                </Box>

                <Box sx={{ minHeight: 20 }}>
                  <Fade in={showSlow} timeout={600}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.55)', fontStyle: 'italic' }}
                      aria-live="polite"
                    >
                      {slowMessage}
                    </Typography>
                  </Fade>
                </Box>

                {quote?.text && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      display: 'block',
                      pt: 1,
                      borderTop: '1px solid rgba(255,255,255,0.08)',
                      width: '100%',
                    }}
                  >
                    “{quote.text}”{quote.author ? ` — ${quote.author}` : ''}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <ErrorOutlineIcon sx={{ fontSize: 52, color: 'secondary.main' }} />
                <Box>
                  <Typography
                    id="save-progress-headline"
                    variant="h5"
                    sx={{ color: 'common.white', mb: 1 }}
                  >
                    {timeoutHeadline}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 340, mx: 'auto' }}
                  >
                    {timeoutBody}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1.5} sx={{ pt: 0.5 }}>
                  {canRetry && (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<ReplayIcon />}
                      onClick={onRetry}
                    >
                      Retry
                    </Button>
                  )}
                  <Button
                    variant={canRetry ? 'outlined' : 'contained'}
                    color={canRetry ? 'inherit' : 'secondary'}
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                    sx={
                      canRetry
                        ? { color: 'common.white', borderColor: 'rgba(255,255,255,0.3)' }
                        : undefined
                    }
                  >
                    Refresh page
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

/**
 * Hook that runs a long-running save operation behind the blocking overlay.
 *
 * Usage:
 *   const { runWithSaveProgress, SaveProgressContainer } = useSaveProgress();
 *   // in JSX: {SaveProgressContainer}
 *   await runWithSaveProgress(() => lockLoan(loanId), { headline: '…' });
 *
 * `operation` may be a function returning a promise (preferred — enables the
 * Retry action) or a promise/thenable. The returned promise settles with the
 * operation's result or error, so callers can `await` it as usual. The overlay
 * auto-dismisses on success or failure, and transitions to a timeout state
 * (with retry/refresh) if the operation does not resolve in time.
 */
export const useSaveProgress = () => {
  const [state, setState] = useState({
    open: false,
    status: 'saving',
    options: {},
  });

  // Refs survive re-renders so timers/attempts can be tracked without churn.
  const runIdRef = useRef(0);
  const timeoutTimerRef = useRef(null);
  const operationRef = useRef(null);
  const deferredRef = useRef(null);

  const clearTimeoutTimer = useCallback(() => {
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  const startAttempt = useCallback(
    (operation, options) => {
      const runId = ++runIdRef.current;
      const canRetry = typeof operation === 'function';
      operationRef.current = canRetry ? operation : null;

      setState({ open: true, status: 'saving', options: { ...options, canRetry } });

      clearTimeoutTimer();
      const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
      timeoutTimerRef.current = setTimeout(() => {
        if (runIdRef.current !== runId) return;
        setState((s) => ({ ...s, status: 'timeout' }));
      }, timeoutMs);

      const settleSuccess = (result) => {
        if (runIdRef.current !== runId) return;
        clearTimeoutTimer();
        setState((s) => ({ ...s, open: false }));
        deferredRef.current?.resolve(result);
      };
      const settleFailure = (error) => {
        if (runIdRef.current !== runId) return;
        clearTimeoutTimer();
        setState((s) => ({ ...s, open: false }));
        deferredRef.current?.reject(error);
      };

      try {
        const result = canRetry ? operation() : operation;
        Promise.resolve(result).then(settleSuccess, settleFailure);
      } catch (error) {
        settleFailure(error);
      }
    },
    [clearTimeoutTimer],
  );

  const runWithSaveProgress = useCallback(
    (operation, options = {}) =>
      new Promise((resolve, reject) => {
        deferredRef.current = { resolve, reject };
        startAttempt(operation, options);
      }),
    [startAttempt],
  );

  const handleRetry = useCallback(() => {
    if (typeof operationRef.current === 'function') {
      startAttempt(operationRef.current, state.options);
    }
  }, [startAttempt, state.options]);

  const handleRefresh = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, []);

  // Clean up any pending timer if the host component unmounts mid-save.
  useEffect(() => () => clearTimeoutTimer(), [clearTimeoutTimer]);

  const { options } = state;
  const SaveProgressContainer = useMemo(
    () => (
      <SaveProgressOverlay
        open={state.open}
        status={state.status}
        headline={options.headline}
        body={options.body}
        messages={options.messages}
        slowMessage={options.slowMessage}
        quote={options.quote}
        timeoutHeadline={options.timeoutHeadline}
        timeoutBody={options.timeoutBody}
        canRetry={!!options.canRetry}
        onRetry={handleRetry}
        onRefresh={handleRefresh}
      />
    ),
    [state.open, state.status, options, handleRetry, handleRefresh],
  );

  return { runWithSaveProgress, SaveProgressContainer };
};

export default SaveProgressOverlay;
